/**
 * Placeable-object registry: the single source the editor palette, inspector,
 * and (later) server-side validation use to know what exists. Adding an
 * entry here is all it takes for a new object to appear in the editor.
 */
import type { LevelObject, ObjectType } from './types';

export interface ObjectDef {
  /** Stable id used for favorites/recents ("block", "portal:form:glider"…). */
  defId: string;
  label: string;
  category: string;
  type: ObjectType;
  /** Default props stamped onto placed objects. */
  props?: Record<string, number | string | boolean>;
  flipY?: boolean;
  /** Search aliases. */
  tags?: string[];
}

const defs: ObjectDef[] = [
  { defId: 'block', label: 'Block', category: 'Blocks', type: 'block', tags: ['solid'] },
  {
    defId: 'slope:up',
    label: 'Slope 45° Up',
    category: 'Blocks',
    type: 'slope',
    props: { dir: 'up', grade: 1 },
    tags: ['ramp'],
  },
  {
    defId: 'slope:down',
    label: 'Slope 45° Down',
    category: 'Blocks',
    type: 'slope',
    props: { dir: 'down', grade: 1 },
    tags: ['ramp'],
  },
  {
    defId: 'slope:up-half',
    label: 'Slope 22° Up',
    category: 'Blocks',
    type: 'slope',
    props: { dir: 'up', grade: 0.5 },
    tags: ['ramp', 'gentle'],
  },
  {
    defId: 'slope:down-half',
    label: 'Slope 22° Down',
    category: 'Blocks',
    type: 'slope',
    props: { dir: 'down', grade: 0.5 },
    tags: ['ramp', 'gentle'],
  },
  {
    defId: 'oneway',
    label: 'One-Way Platform',
    category: 'Blocks',
    type: 'oneway',
    tags: ['platform', 'passthrough'],
  },
  { defId: 'spike', label: 'Spike', category: 'Hazards', type: 'spike', tags: ['kill', 'hazard'] },
  {
    defId: 'spike:hanging',
    label: 'Hanging Spike',
    category: 'Hazards',
    type: 'spike',
    flipY: true,
    tags: ['kill', 'ceiling'],
  },
  {
    defId: 'decoration',
    label: 'Pylon',
    category: 'Decoration',
    type: 'decoration',
    props: { kind: 'pylon' },
    tags: ['deco', 'light'],
  },
];

const FORM_LABELS: Record<string, string> = {
  runner: 'Runner',
  glider: 'Glider',
  roller: 'Roller',
  hopper: 'Hopper',
  streak: 'Streak',
  vaulter: 'Vaulter',
  blinker: 'Blinker',
  pendul: 'Pendul',
};
for (const [form, label] of Object.entries(FORM_LABELS)) {
  defs.push({
    defId: `portal:form:${form}`,
    label: `${label} Portal`,
    category: 'Form Portals',
    type: 'portal',
    props: { kind: `form:${form}` },
    tags: ['portal', 'form', form],
  });
}

const PORTAL_LABELS: Array<[string, string]> = [
  ['gravity:up', 'Gravity Up Portal'],
  ['gravity:down', 'Gravity Down Portal'],
  ['speed:half', 'Speed ½× Portal'],
  ['speed:one', 'Speed 1× Portal'],
  ['speed:two', 'Speed 2× Portal'],
  ['speed:three', 'Speed 3× Portal'],
  ['speed:four', 'Speed 4× Portal'],
  ['size:mini', 'Mini Portal'],
  ['size:normal', 'Normal Size Portal'],
  ['mirror:on', 'Mirror On Portal'],
  ['mirror:off', 'Mirror Off Portal'],
  ['dual:on', 'Dual On Portal'],
  ['dual:off', 'Dual Off Portal'],
];
for (const [kind, label] of PORTAL_LABELS) {
  defs.push({
    defId: `portal:${kind}`,
    label,
    category: 'Portals',
    type: 'portal',
    props: { kind },
    tags: ['portal'],
  });
}
defs.push({
  defId: 'portal:teleport',
  label: 'Teleporter',
  category: 'Portals',
  type: 'portal',
  props: { kind: 'teleport', dx: 0, dy: 4 },
  tags: ['portal', 'warp'],
});

const PAD_LABELS: Array<[string, string]> = [
  ['strong', 'Launch Pad'],
  ['soft', 'Soft Pad'],
  ['mega', 'Mega Pad'],
  ['flip', 'Gravity Pad'],
];
for (const [kind, label] of PAD_LABELS) {
  defs.push({
    defId: `pad:${kind}`,
    label,
    category: 'Pads & Orbs',
    type: 'pad',
    props: { kind },
    tags: ['pad', 'bounce'],
  });
}

const ORB_LABELS: Array<[string, string]> = [
  ['strong', 'Launch Orb'],
  ['soft', 'Soft Orb'],
  ['mega', 'Mega Orb'],
  ['flip', 'Gravity Orb'],
  ['flip-boost', 'Boost Flip Orb'],
  ['slam', 'Slam Orb'],
  ['blink', 'Blink Orb'],
  ['dash', 'Dash Orb'],
  ['gravdash', 'Gravity Dash Orb'],
];
for (const [kind, label] of ORB_LABELS) {
  defs.push({
    defId: `orb:${kind}`,
    label,
    category: 'Pads & Orbs',
    type: 'orb',
    props: { kind },
    tags: ['orb', 'tap'],
  });
}

const TRIGGER_DEFAULTS: Array<[string, string, Record<string, number | string | boolean>]> = [
  ['move', 'Move Trigger', { target: 1, dx: 0, dy: 2, duration: 1, easing: 'inout' }],
  ['rotate', 'Rotate Trigger', { target: 1, degrees: 90, duration: 1, easing: 'inout' }],
  ['alpha', 'Alpha Trigger', { target: 1, alpha: 0, duration: 0.5 }],
  ['toggle', 'Toggle Trigger', { target: 1, visible: false }],
  ['color', 'Color Trigger', { channel: 1, hex: '#f0abfc', duration: 0.5 }],
  ['pulse', 'Pulse Trigger', { channel: 1, hex: '#ffffff', duration: 0.4 }],
  ['scale', 'Scale Trigger', { target: 1, scale: 1.5, duration: 1, easing: 'inout' }],
  ['followy', 'Follow-Y Trigger', { target: 1, duration: 2, factor: 1 }],
  ['spawn', 'Spawn Trigger', { target: 1, delay: 0.5 }],
  ['stop', 'Stop Trigger', { target: 1 }],
  ['pause', 'Pause Trigger', { target: 1 }],
  ['resume', 'Resume Trigger', { target: 1 }],
  ['camera:zoom', 'Camera Zoom', { zoom: 1.3, duration: 1, easing: 'inout' }],
  ['camera:offset', 'Camera Offset', { dx: 0, dy: 3, duration: 1, easing: 'inout' }],
  ['camera:shake', 'Camera Shake', { strength: 8, duration: 0.5 }],
  ['camera:rotate', 'Camera Rotate', { degrees: 10, duration: 1, easing: 'inout' }],
  ['camera:static', 'Camera Lock', { dx: 0, dy: 6 }],
  ['camera:free', 'Camera Release', {}],
  ['timewarp', 'Time Warp', { factor: 0.5 }],
  ['shader:chromatic', 'FX: Chromatic', { strength: 0.6, duration: 0.5 }],
  ['shader:pixelate', 'FX: Pixelate', { strength: 8, duration: 0.5 }],
  ['shader:grayscale', 'FX: Grayscale', { strength: 1, duration: 0.5 }],
  ['shader:sepia', 'FX: Sepia', { strength: 1, duration: 0.5 }],
  ['shader:invert', 'FX: Invert', { strength: 1, duration: 0.5 }],
  ['shader:hue', 'FX: Hue Shift', { strength: 120, duration: 0.5 }],
  ['shader:bulge', 'FX: Bulge', { strength: 0.4, duration: 0.5 }],
  ['shader:shockwave', 'FX: Shockwave', { strength: 1, duration: 0.8 }],
  ['shader:shockline', 'FX: Shockline', { strength: 1, duration: 0.6 }],
  ['shader:glitch', 'FX: Glitch', { strength: 0.6, duration: 0.5 }],
  ['shader:radial', 'FX: Radial Blur', { strength: 0.6, duration: 0.5 }],
  ['shader:motion', 'FX: Motion Blur', { strength: 0.6, duration: 0.5 }],
  ['shader:lens', 'FX: Lens Circle', { strength: 0.8, duration: 0.5 }],
  ['shader:split', 'FX: Split Screen', { strength: 1, duration: 0.5 }],
  ['area:move', 'Area Move', { radius: 6, dx: 0, dy: 1 }],
  ['area:rotate', 'Area Rotate', { radius: 6, degrees: 45 }],
  ['area:scale', 'Area Scale', { radius: 6, scale: 1.5 }],
  ['area:fade', 'Area Fade', { radius: 6, alpha: 0.2 }],
  ['area:tint', 'Area Tint', { radius: 6, hex: '#f0abfc' }],
  ['enter:move', 'Enter: Move In', { radius: 8, dx: 0, dy: 4 }],
  ['enter:rotate', 'Enter: Spin In', { radius: 8, degrees: 180 }],
  ['enter:scale', 'Enter: Grow In', { radius: 8, scale: 0.2 }],
  ['enter:fade', 'Enter: Fade In', { radius: 8 }],
  ['enter:off', 'Enter: Off', {}],
  [
    'gradient',
    'Gradient Overlay',
    { hex: '#22d3ee', hex2: '#f0abfc', alpha: 0.25, vertical: true },
  ],
  ['gradient:off', 'Gradient Off', {}],
  [
    'keyframe',
    'Keyframe Path',
    { target: 1, path: '0,2,0.5;2,0,0.5;0,-2,0.5;-2,0,0.5', loop: true },
  ],
  ['camera:edge', 'Camera Edge', { side: 'bottom', dy: 0 }],
  ['camera:edge-off', 'Camera Edge Off', {}],
  ['end', 'End Trigger', {}],
  ['pickup', 'Pickup Trigger', { target: 1, factor: 1 }],
  ['item:spawn-if', 'Item Spawn-If', { target: 1, factor: 1, value: 1 }],
];
for (const [kind, label, props] of TRIGGER_DEFAULTS) {
  defs.push({
    defId: `trigger:${kind}`,
    label,
    category: 'Triggers',
    type: 'trigger',
    props: { kind, ...props },
    tags: ['trigger', kind.replace('camera:', 'camera ')],
  });
}

defs.push(
  {
    defId: 'collectible:key',
    label: 'Key',
    category: 'Collectibles',
    type: 'collectible',
    props: { kind: 'key', itemId: 1 },
    tags: ['key', 'lock', 'item'],
  },
  {
    defId: 'collectible:shard',
    label: 'Secret Shard',
    category: 'Collectibles',
    type: 'collectible',
    props: { kind: 'shard' },
    tags: ['secret', 'coin'],
  },
  {
    defId: 'collectible:mark',
    label: 'Mark',
    category: 'Collectibles',
    type: 'collectible',
    props: { kind: 'mark' },
    tags: ['coin', 'user'],
  },
  {
    defId: 'lock',
    label: 'Lock Door',
    category: 'Collectibles',
    type: 'lock',
    props: { itemId: 1, needed: 1 },
    tags: ['key', 'door'],
  },
  {
    defId: 'text',
    label: 'Text',
    category: 'Decoration',
    type: 'text',
    props: { text: 'NEON', size: 1 },
    tags: ['label', 'words', 'tutorial'],
  },
  {
    defId: 'checkpoint',
    label: 'Checkpoint',
    category: 'Helpers',
    type: 'checkpoint',
    tags: ['platformer', 'respawn'],
  },
  {
    defId: 'endgate',
    label: 'End Gate',
    category: 'Helpers',
    type: 'endgate',
    tags: ['finish', 'goal'],
  },
  {
    defId: 'startpos',
    label: 'Start Position',
    category: 'Helpers',
    type: 'startpos',
    tags: ['spawn', 'playtest'],
  },
);

export const OBJECT_DEFS: readonly ObjectDef[] = defs;

export const CATEGORIES: readonly string[] = [...new Set(defs.map((d) => d.category))];

export function defsByCategory(category: string): ObjectDef[] {
  return defs.filter((d) => d.category === category);
}

export function searchDefs(query: string): ObjectDef[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...defs];
  return defs.filter(
    (d) =>
      d.label.toLowerCase().includes(q) ||
      d.defId.includes(q) ||
      (d.tags ?? []).some((t) => t.includes(q)),
  );
}

/** Instantiate a def as a level object (id assigned by the editor store). */
export function instantiate(def: ObjectDef, id: number, x: number, y: number): LevelObject {
  const obj: LevelObject = { id, type: def.type, x, y };
  if (def.props && Object.keys(def.props).length > 0) obj.props = { ...def.props };
  if (def.flipY) obj.flipY = true;
  return obj;
}
